import { LOGO_SBG, LOGO_PT, LOGO_SBG_COLOR } from "@/lib/logos";

export const CANVAS_SIZE = 1080;
const YEAR = 2026;
const FD = "Jost, 'Futura PT', 'Century Gothic', sans-serif";

// Southern Black Girls palette (yellow allowed)
const C = { root: "#3A0300", wine: "#AD1C5D", fuchsia: "#E02189", blush: "#F8D4DE", joy: "#FDF71E", aqua: "#67DAD7" };
// Pete & Thomas partner palette (purple + pink, NO yellow)
const P = { deep: "#400057", lav: "#9d84af", lavTint: "#EDE6F2", plum: "#7A1F7E", pink: "#E02189", blush: "#F8D4DE" };

// Frame registry. group drives the two UI columns + which logos appear.
export const FRAMES = [
  { id: "ring", group: "sbg", label: "Joy Ring" },
  { id: "banner", group: "sbg", label: "Winner Banner" },
  { id: "card", group: "sbg", label: "Blush Card" },
  { id: "sun", group: "sbg", label: "Sunshine" },
  { id: "royal", group: "pt", label: "Royal Ring" },
  { id: "twilight", group: "pt", label: "Twilight Banner" },
  { id: "lavender", group: "pt", label: "Lavender Card" },
  { id: "arch", group: "pt", label: "Purple Arch" },
  { id: "pRing", group: "pt", label: "Partner Ring" },
  { id: "pBanner", group: "pt", label: "Partner Banner" },
  { id: "pPoster", group: "pt", label: "Partner Poster" },
];

export function getFrame(id) {
  return FRAMES.find((f) => f.id === id) || FRAMES[0];
}

// swatch preview color hints for FrameEditor
export const SWATCH = {
  ring: { bg: C.root, accent: C.joy }, banner: { bg: C.root, accent: C.fuchsia },
  card: { bg: C.blush, accent: C.fuchsia }, sun: { bg: C.joy, accent: C.fuchsia },
  royal: { bg: P.deep, accent: P.pink }, twilight: { bg: P.deep, accent: P.lav },
  lavender: { bg: P.lavTint, accent: P.pink }, arch: { bg: P.deep, accent: P.pink },
  pRing: { bg: P.deep, accent: P.blush }, pBanner: { bg: P.deep, accent: P.pink },
  pPoster: { bg: P.deep, accent: P.lav },
};

// ---- preloaded logos (data URLs -> no canvas taint) ----
const LOGO_IMG = { sbg: null, pt: null, sbgColor: null };
let resolveLogos;
export const logosReady = new Promise((r) => (resolveLogos = r));
if (typeof window !== "undefined") {
  let left = 3;
  const done = () => { if (--left <= 0) resolveLogos(); };
  const mk = (src) => { const im = new Image(); im.onload = done; im.onerror = done; im.src = src; return im; };
  LOGO_IMG.sbg = mk(LOGO_SBG);
  LOGO_IMG.pt = mk(LOGO_PT);
  LOGO_IMG.sbgColor = mk(LOGO_SBG_COLOR);
} else {
  resolveLogos && resolveLogos();
}

// ---------- helpers (ported from original toolkit) ----------
function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}
function setSpacing(ctx, px) { try { ctx.letterSpacing = px + "px"; } catch (e) { /* letterSpacing unsupported in this browser */ } }
function fitSize(ctx, text, weight, family, max, maxW, min = 18) {
  let s = max; ctx.font = `${weight} ${s}px ${family}`;
  while (s > min && ctx.measureText(text).width > maxW) { s -= 2; ctx.font = `${weight} ${s}px ${family}`; }
  return s;
}
function star(ctx, cx, cy, outer, inner, pts, rot = -Math.PI / 2) {
  ctx.beginPath();
  for (let i = 0; i < pts * 2; i++) { const r = i % 2 ? inner : outer, a = rot + (i * Math.PI) / pts; ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a)); }
  ctx.closePath();
}
function sparkle(ctx, cx, cy, r) {
  ctx.beginPath(); ctx.moveTo(cx, cy - r);
  ctx.quadraticCurveTo(cx, cy, cx + r, cy); ctx.quadraticCurveTo(cx, cy, cx, cy + r);
  ctx.quadraticCurveTo(cx, cy, cx - r, cy); ctx.quadraticCurveTo(cx, cy, cx, cy - r);
  ctx.closePath(); ctx.fill();
}
function archPath(ctx, x, y, w, h) {
  const r = w / 2; ctx.moveTo(x, y + h); ctx.lineTo(x, y + r);
  ctx.arc(x + r, y + r, r, Math.PI, 0); ctx.lineTo(x + w, y + h); ctx.closePath();
}
function arcText(ctx, text, cx, cy, r, onTop, size, spacing, maxArc) {
  let ws, total;
  const measure = () => { ctx.font = `600 ${size}px ${FD}`; ws = [...text].map((ch) => ctx.measureText(ch).width); total = ws.reduce((a, b) => a + b, 0) + spacing * (ws.length - 1); };
  measure(); while (total > maxArc && size > 20) { size -= 2; spacing = Math.max(1, spacing - 0.5); measure(); }
  ctx.textAlign = "center"; ctx.textBaseline = "middle"; setSpacing(ctx, 0);
  const center = onTop ? -Math.PI / 2 : Math.PI / 2;
  let ang = onTop ? center - total / r / 2 : center + total / r / 2;
  [...text].forEach((ch, i) => {
    const half = ws[i] / 2;
    const a = onTop ? ang + half / r : ang - half / r;
    ctx.save(); ctx.translate(cx + r * Math.cos(a), cy + r * Math.sin(a)); ctx.rotate(onTop ? a + Math.PI / 2 : a - Math.PI / 2);
    ctx.fillText(ch, 0, 0); ctx.restore();
    ang = onTop ? ang + (ws[i] + spacing) / r : ang - (ws[i] + spacing) / r;
  });
}
function drawLogo(ctx, img, x, y, h) {
  if (img && img.complete && img.naturalWidth) { const w = (h * img.naturalWidth) / img.naturalHeight; ctx.drawImage(img, x, y, w, h); return w; }
  return 0;
}
function logoPill(ctx, img, cx, cy, h, bg) {
  const w = img && img.naturalWidth ? (h * img.naturalWidth) / img.naturalHeight : h * 2.26;
  const pw = w + h * 0.9, ph = h * 1.45;
  ctx.fillStyle = bg; rr(ctx, cx - pw / 2, cy - ph / 2, pw, ph, ph / 2); ctx.fill();
  drawLogo(ctx, img, cx - w / 2, cy - h / 2, h);
}
// combined SBG + P&T lockup, centered at cx, both logos height h
function drawLockup(ctx, cx, cy, h) {
  const sbg = LOGO_IMG.sbg, pt = LOGO_IMG.pt;
  const gap = h * 0.5;
  const wS = sbg && sbg.naturalWidth ? (h * sbg.naturalWidth) / sbg.naturalHeight : h * 2.26;
  const wP = pt && pt.naturalWidth ? (h * pt.naturalWidth) / pt.naturalHeight : h * 2.26;
  const total = wS + gap + wP;
  let x = cx - total / 2;
  drawLogo(ctx, sbg, x, cy - h / 2, h); x += wS + gap;
  drawLogo(ctx, pt, x, cy - h / 2, h);
}
// lockup sitting on a rounded badge so logos read on any background
function drawLockupOnPill(ctx, cx, cy, h, bg) {
  const sbg = LOGO_IMG.sbg, pt = LOGO_IMG.pt;
  const gap = h * 0.5;
  const wS = sbg && sbg.naturalWidth ? (h * sbg.naturalWidth) / sbg.naturalHeight : h * 2.26;
  const wP = pt && pt.naturalWidth ? (h * pt.naturalWidth) / pt.naturalHeight : h * 2.26;
  const total = wS + gap + wP;
  const padX = h * 0.7, padY = h * 0.55;
  const pw = total + padX * 2, ph = h + padY * 2;
  ctx.fillStyle = bg; rr(ctx, cx - pw / 2, cy - ph / 2, pw, ph, ph / 2); ctx.fill();
  drawLockup(ctx, cx, cy, h);
}

const REGIONS = {
  ring: { x: 110, y: 110, w: 860, h: 860, shape: "circle" },
  banner: { x: 0, y: 0, w: 1080, h: 830, shape: "rect" },
  card: { x: 90, y: 270, w: 900, h: 560, shape: "round", r: 40 },
  sun: { x: 200, y: 180, w: 680, h: 680, shape: "circle" },
  royal: { x: 120, y: 120, w: 840, h: 840, shape: "circle" },
  twilight: { x: 0, y: 0, w: 1080, h: 820, shape: "rect" },
  lavender: { x: 120, y: 175, w: 840, h: 615, shape: "round", r: 36 },
  arch: { x: 220, y: 200, w: 640, h: 650, shape: "arch" },
  pRing: { x: 220, y: 100, w: 640, h: 640, shape: "circle" },
  pBanner: { x: 0, y: 0, w: 1080, h: 740, shape: "rect" },
  pPoster: { x: 150, y: 180, w: 780, h: 600, shape: "round", r: 40 },
};
function clipRegion(ctx, g) {
  ctx.beginPath();
  if (g.shape === "circle") ctx.arc(g.x + g.w / 2, g.y + g.h / 2, g.w / 2, 0, Math.PI * 2);
  else if (g.shape === "round") rr(ctx, g.x, g.y, g.w, g.h, g.r);
  else if (g.shape === "arch") archPath(ctx, g.x, g.y, g.w, g.h);
  else ctx.rect(g.x, g.y, g.w, g.h);
  ctx.clip();
}
// S: { img, zoom, rot, flip, offsetX, offsetY } — offsets in canvas px
function drawPhoto(ctx, g, S) {
  ctx.save(); clipRegion(ctx, g);
  if (!S.img) {
    ctx.fillStyle = C.blush; ctx.fillRect(g.x, g.y, g.w, g.h);
    const cx = g.x + g.w / 2, cy = g.y + g.h / 2, u = Math.min(g.w, g.h);
    ctx.fillStyle = "rgba(173,28,93,.28)";
    ctx.beginPath(); ctx.arc(cx, cy - u * 0.12, u * 0.17, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(cx, cy + u * 0.36, u * 0.32, u * 0.26, 0, Math.PI, 0); ctx.fill();
  } else {
    const iw = S.img.naturalWidth || S.img.width, ih = S.img.naturalHeight || S.img.height;
    const base = Math.max(g.w / iw, g.h / ih) * S.zoom;
    ctx.translate(g.x + g.w / 2 + S.offsetX, g.y + g.h / 2 + S.offsetY);
    ctx.rotate((S.rot * Math.PI) / 180);
    ctx.scale(S.flip ? -base : base, base);
    ctx.drawImage(S.img, -iw / 2, -ih / 2);
  }
  ctx.restore();
}

// Each frame: draw(ctx, d, S) where d = { name, showName }
const DRAW = {
  royal(ctx, d, S) {
    ctx.fillStyle = P.deep; ctx.fillRect(0, 0, 1080, 1080);
    const g = ctx.createLinearGradient(0, 1080, 1080, 0); g.addColorStop(0, P.pink); g.addColorStop(0.5, P.plum); g.addColorStop(1, P.lav);
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(540, 540, 540, 0, Math.PI * 2); ctx.fill();
    drawPhoto(ctx, REGIONS.royal, S);
    ctx.strokeStyle = P.blush; ctx.lineWidth = 8; ctx.beginPath(); ctx.arc(540, 540, 424, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = "#fff";
    arcText(ctx, d.showName ? d.name.toUpperCase() : `I'M A ${YEAR} WINNER`, 540, 540, 484, true, 62, 6, 1250);
    arcText(ctx, d.showName ? `#BLACKGIRLJOYCHALLENGE · ${YEAR} WINNER` : "#BLACKGIRLJOYCHALLENGE", 540, 540, 484, false, 56, 5, 1250);
    ctx.fillStyle = P.blush; sparkle(ctx, 50, 540, 26); sparkle(ctx, 1030, 540, 26);
    drawLockupOnPill(ctx, 540, 862, 66, P.deep);
  },
  twilight(ctx, d, S) {
    drawPhoto(ctx, REGIONS.twilight, S);
    const g = ctx.createLinearGradient(0, 0, 1080, 0); g.addColorStop(0, P.deep); g.addColorStop(0.6, P.plum); g.addColorStop(1, P.pink);
    ctx.fillStyle = g; ctx.fillRect(0, 820, 1080, 260);
    ctx.fillStyle = P.lav; ctx.fillRect(0, 820, 1080, 12);
    drawLockup(ctx, 300, 950, 92);
    const tx = 560, maxW = 1080 - tx - 50;
    ctx.textAlign = "left"; ctx.textBaseline = "alphabetic"; setSpacing(ctx, 0);
    ctx.fillStyle = "#fff"; fitSize(ctx, "#BlackGirlJoyChallenge", "700", FD, 56, maxW); ctx.fillText("#BlackGirlJoyChallenge", tx, 952);
    ctx.fillStyle = P.blush; setSpacing(ctx, 8); fitSize(ctx, `${YEAR} WINNER`, "500", FD, 38, maxW); ctx.fillText(`${YEAR} WINNER`, tx, 1012); setSpacing(ctx, 0);
    if (d.showName) {
      const s = fitSize(ctx, d.name, "600", FD, 50, 900); const w = ctx.measureText(d.name).width;
      ctx.fillStyle = P.deep; rr(ctx, 50, 734, w + 64, s + 40, (s + 40) / 2); ctx.fill();
      ctx.strokeStyle = P.pink; ctx.lineWidth = 4; ctx.stroke();
      ctx.fillStyle = "#fff"; ctx.textBaseline = "middle"; ctx.fillText(d.name, 82, 734 + (s + 40) / 2 + 2);
    }
  },
  lavender(ctx, d, S) {
    ctx.fillStyle = P.lavTint; ctx.fillRect(0, 0, 1080, 1080);
    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    ctx.fillStyle = P.plum; setSpacing(ctx, 10); ctx.font = `500 34px ${FD}`; ctx.fillText(`I'M A ${YEAR}`, 540, 68); setSpacing(ctx, 0);
    ctx.fillStyle = P.deep; fitSize(ctx, "#BlackGirlJoyChallenge Winner", "700", FD, 68, 920); ctx.fillText("#BlackGirlJoyChallenge Winner", 540, 132);
    ctx.fillStyle = P.pink; rr(ctx, 140, 195, 840, 615, 36); ctx.fill();
    drawPhoto(ctx, REGIONS.lavender, S);
    ctx.strokeStyle = P.deep; ctx.lineWidth = 8; rr(ctx, 120, 175, 840, 615, 36); ctx.stroke();
    ctx.fillStyle = P.deep; ctx.fillRect(0, 820, 1080, 260);
    ctx.fillStyle = P.lav; ctx.fillRect(0, 820, 1080, 8);
    if (d.showName) {
      ctx.textAlign = "center"; ctx.textBaseline = "alphabetic"; ctx.fillStyle = "#fff";
      fitSize(ctx, d.name, "700", FD, 48, 900); ctx.fillText(d.name, 540, 902);
      drawLockup(ctx, 540, 992, 66);
    } else {
      drawLockup(ctx, 540, 950, 80);
    }
  },
  arch(ctx, d, S) {
    ctx.fillStyle = P.deep; ctx.fillRect(0, 0, 1080, 1080);
    const rg = ctx.createRadialGradient(1000, 1000, 0, 1000, 1000, 760); rg.addColorStop(0, "rgba(224,33,137,.55)"); rg.addColorStop(1, "rgba(224,33,137,0)");
    ctx.fillStyle = rg; ctx.fillRect(0, 0, 1080, 1080);
    ctx.fillStyle = P.lav; ctx.beginPath(); archPath(ctx, 252, 176, 640, 650); ctx.fill();
    drawPhoto(ctx, REGIONS.arch, S);
    ctx.strokeStyle = P.pink; ctx.lineWidth = 8; ctx.beginPath(); archPath(ctx, 220, 200, 640, 650); ctx.stroke();
    drawLockup(ctx, 540, 100, 84);
    ctx.fillStyle = P.blush; sparkle(ctx, 150, 520, 24); sparkle(ctx, 118, 600, 13); sparkle(ctx, 950, 420, 18);
    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    if (d.showName) {
      ctx.fillStyle = "#fff"; fitSize(ctx, d.name, "700", FD, 64, 960); ctx.fillText(d.name, 540, 948);
      ctx.fillStyle = P.blush; fitSize(ctx, "#BlackGirlJoyChallenge Winner", "500", FD, 38, 960); ctx.fillText("#BlackGirlJoyChallenge Winner", 540, 1012);
    } else {
      ctx.fillStyle = "#fff"; fitSize(ctx, "#BlackGirlJoyChallenge", "700", FD, 64, 960); ctx.fillText("#BlackGirlJoyChallenge", 540, 952);
      ctx.fillStyle = P.blush; setSpacing(ctx, 8); ctx.font = `500 36px ${FD}`; ctx.fillText(`${YEAR} WINNER`, 540, 1014); setSpacing(ctx, 0);
    }
  },
  pRing(ctx, d, S) {
    ctx.fillStyle = P.deep; ctx.fillRect(0, 0, 1080, 1080);
    const rg = ctx.createRadialGradient(540, 420, 200, 540, 420, 640); rg.addColorStop(0, "rgba(224,33,137,.35)"); rg.addColorStop(1, "rgba(224,33,137,0)");
    ctx.fillStyle = rg; ctx.fillRect(0, 0, 1080, 1080);
    const g = ctx.createLinearGradient(200, 760, 880, 80); g.addColorStop(0, P.pink); g.addColorStop(0.5, P.plum); g.addColorStop(1, P.lav);
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(540, 420, 352, 0, Math.PI * 2); ctx.fill();
    drawPhoto(ctx, REGIONS.pRing, S);
    ctx.strokeStyle = P.blush; ctx.lineWidth = 6; ctx.beginPath(); ctx.arc(540, 420, 320, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = P.blush; sparkle(ctx, 150, 250, 24); sparkle(ctx, 118, 330, 12); sparkle(ctx, 935, 560, 20);
    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic"; setSpacing(ctx, 0);
    if (d.showName) {
      ctx.fillStyle = "#fff"; fitSize(ctx, d.name, "700", FD, 60, 940); ctx.fillText(d.name, 540, 836);
      ctx.fillStyle = P.blush; fitSize(ctx, `${YEAR} #BlackGirlJoyChallenge Winner`, "500", FD, 36, 940); ctx.fillText(`${YEAR} #BlackGirlJoyChallenge Winner`, 540, 886);
    } else {
      ctx.fillStyle = "#fff"; fitSize(ctx, "#BlackGirlJoyChallenge", "700", FD, 60, 940); ctx.fillText("#BlackGirlJoyChallenge", 540, 836);
      ctx.fillStyle = P.blush; setSpacing(ctx, 8); ctx.font = `500 34px ${FD}`; ctx.fillText(`${YEAR} WINNER`, 540, 886); setSpacing(ctx, 0);
    }
    drawLockup(ctx, 540, 980, 88);
  },
  pBanner(ctx, d, S) {
    drawPhoto(ctx, REGIONS.pBanner, S);
    ctx.fillStyle = P.deep; ctx.fillRect(0, 740, 1080, 340);
    const g = ctx.createLinearGradient(0, 0, 1080, 0); g.addColorStop(0, P.deep); g.addColorStop(0.55, P.plum); g.addColorStop(1, P.pink);
    ctx.fillStyle = g; ctx.fillRect(0, 740, 1080, 12);
    ctx.textAlign = "center"; ctx.textBaseline = "middle"; setSpacing(ctx, 0);
    ctx.fillStyle = "#fff"; fitSize(ctx, "#BlackGirlJoyChallenge", "700", FD, 60, 980); ctx.fillText("#BlackGirlJoyChallenge", 540, 812);
    ctx.fillStyle = P.blush; setSpacing(ctx, 8); fitSize(ctx, `${YEAR} WINNER`, "500", FD, 40, 980); ctx.fillText(`${YEAR} WINNER`, 540, 866); setSpacing(ctx, 0);
    drawLockup(ctx, 540, 968, 86);
    if (d.showName) {
      ctx.textAlign = "left";
      const s = fitSize(ctx, d.name, "600", FD, 50, 900), w = ctx.measureText(d.name).width;
      ctx.fillStyle = P.pink; rr(ctx, 50, 650, w + 64, s + 40, (s + 40) / 2); ctx.fill();
      ctx.strokeStyle = P.blush; ctx.lineWidth = 4; ctx.stroke();
      ctx.fillStyle = "#fff"; ctx.textBaseline = "middle"; ctx.fillText(d.name, 82, 650 + (s + 40) / 2 + 2);
    }
  },
  pPoster(ctx, d, S) {
    ctx.fillStyle = P.deep; ctx.fillRect(0, 0, 1080, 1080);
    const rg = ctx.createRadialGradient(80, 1040, 0, 80, 1040, 700); rg.addColorStop(0, "rgba(224,33,137,.5)"); rg.addColorStop(1, "rgba(224,33,137,0)");
    ctx.fillStyle = rg; ctx.fillRect(0, 0, 1080, 1080);
    drawLockup(ctx, 540, 104, 90);
    ctx.fillStyle = P.pink; rr(ctx, 172, 202, 780, 600, 40); ctx.fill();
    drawPhoto(ctx, REGIONS.pPoster, S);
    ctx.strokeStyle = P.lav; ctx.lineWidth = 6; rr(ctx, 150, 180, 780, 600, 40); ctx.stroke();
    ctx.fillStyle = P.blush; sparkle(ctx, 96, 300, 26); sparkle(ctx, 70, 380, 13); sparkle(ctx, 1000, 700, 20);
    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic"; setSpacing(ctx, 0);
    if (d.showName) {
      ctx.fillStyle = "#fff"; fitSize(ctx, d.name, "700", FD, 64, 960); ctx.fillText(d.name, 540, 916);
      ctx.fillStyle = P.blush; fitSize(ctx, `${YEAR} #BlackGirlJoyChallenge Winner`, "500", FD, 38, 960); ctx.fillText(`${YEAR} #BlackGirlJoyChallenge Winner`, 540, 982);
    } else {
      ctx.fillStyle = "#fff"; fitSize(ctx, "#BlackGirlJoyChallenge", "700", FD, 64, 960); ctx.fillText("#BlackGirlJoyChallenge", 540, 916);
      ctx.fillStyle = P.blush; setSpacing(ctx, 8); ctx.font = `500 36px ${FD}`; ctx.fillText(`${YEAR} WINNER`, 540, 982); setSpacing(ctx, 0);
    }
  },
  ring(ctx, d, S) {
    ctx.fillStyle = C.root; ctx.fillRect(0, 0, 1080, 1080);
    const g = ctx.createLinearGradient(0, 0, 1080, 1080); g.addColorStop(0, C.fuchsia); g.addColorStop(0.55, C.wine); g.addColorStop(1, "#6b0a2a");
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(540, 540, 540, 0, Math.PI * 2); ctx.fill();
    drawPhoto(ctx, REGIONS.ring, S);
    ctx.strokeStyle = C.joy; ctx.lineWidth = 12; ctx.beginPath(); ctx.arc(540, 540, 434, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = "#fff";
    arcText(ctx, d.showName ? d.name.toUpperCase() : `I'M A ${YEAR} WINNER`, 540, 540, 488, true, 54, 5, 1250);
    arcText(ctx, d.showName ? `#BLACKGIRLJOYCHALLENGE · ${YEAR} WINNER` : "#BLACKGIRLJOYCHALLENGE", 540, 540, 488, false, 50, 4, 1250);
    ctx.fillStyle = C.joy; star(ctx, 52, 540, 26, 11, 5); ctx.fill(); star(ctx, 1028, 540, 26, 11, 5); ctx.fill();
    logoPill(ctx, LOGO_IMG.sbg, 540, 862, 74, C.root);
  },
  banner(ctx, d, S) {
    drawPhoto(ctx, REGIONS.banner, S);
    ctx.fillStyle = C.root; ctx.fillRect(0, 830, 1080, 250);
    ctx.fillStyle = C.fuchsia; ctx.fillRect(0, 830, 1080, 14);
    const lw = drawLogo(ctx, LOGO_IMG.sbg, 50, 885, 150);
    const tx = 50 + (lw || 330) + 40, maxW = 1080 - tx - 50;
    ctx.textAlign = "left"; ctx.textBaseline = "alphabetic"; ctx.fillStyle = C.joy; setSpacing(ctx, 0);
    fitSize(ctx, "#BlackGirlJoyChallenge", "700", FD, 64, maxW); ctx.fillText("#BlackGirlJoyChallenge", tx, 965);
    ctx.fillStyle = C.blush; setSpacing(ctx, 8); fitSize(ctx, `${YEAR} WINNER`, "600", FD, 44, maxW); ctx.fillText(`${YEAR} WINNER`, tx, 1030); setSpacing(ctx, 0);
    if (d.showName) {
      const s = fitSize(ctx, d.name, "600", FD, 52, 900); const w = ctx.measureText(d.name).width;
      ctx.fillStyle = C.fuchsia; rr(ctx, 50, 742, w + 64, s + 40, (s + 40) / 2); ctx.fill();
      ctx.fillStyle = "#fff"; ctx.textBaseline = "middle"; ctx.fillText(d.name, 82, 742 + (s + 40) / 2 + 2);
    }
  },
  card(ctx, d, S) {
    ctx.fillStyle = C.blush; ctx.fillRect(0, 0, 1080, 1080);
    ctx.fillStyle = C.joy; rr(ctx, 108, 288, 900, 560, 40); ctx.fill();
    drawPhoto(ctx, REGIONS.card, S);
    ctx.strokeStyle = C.fuchsia; ctx.lineWidth = 10; rr(ctx, 90, 270, 900, 560, 40); ctx.stroke();
    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    ctx.fillStyle = C.wine; setSpacing(ctx, 10); ctx.font = `600 40px ${FD}`; ctx.fillText(`I'M A ${YEAR}`, 540, 112); setSpacing(ctx, 0);
    ctx.fillStyle = C.root; fitSize(ctx, "#BlackGirlJoyChallenge Winner", "700", FD, 78, 920); ctx.fillText("#BlackGirlJoyChallenge Winner", 540, 214);
    const lw = drawLogo(ctx, LOGO_IMG.sbgColor, 90, 925, 100);
    if (d.showName) { ctx.textAlign = "right"; ctx.fillStyle = C.root; fitSize(ctx, d.name, "700", FD, 56, 990 - (90 + (lw || 226) + 40)); ctx.fillText(d.name, 990, 985); }
  },
  sun(ctx, d, S) {
    ctx.fillStyle = C.joy; ctx.fillRect(0, 0, 1080, 1080);
    ctx.save(); ctx.translate(540, 520); ctx.fillStyle = "rgba(255,255,255,.45)";
    for (let i = 0; i < 24; i += 2) { const a1 = (i * Math.PI) / 12, a2 = ((i + 1) * Math.PI) / 12; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(1100 * Math.cos(a1), 1100 * Math.sin(a1)); ctx.lineTo(1100 * Math.cos(a2), 1100 * Math.sin(a2)); ctx.closePath(); ctx.fill(); }
    ctx.restore();
    ctx.fillStyle = C.aqua; ctx.beginPath(); ctx.arc(552, 532, 372, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = C.fuchsia; ctx.beginPath(); ctx.arc(540, 520, 372, 0, Math.PI * 2); ctx.fill();
    drawPhoto(ctx, REGIONS.sun, S);
    drawLogo(ctx, LOGO_IMG.sbgColor, 44, 40, 86);
    ctx.fillStyle = C.wine; star(ctx, 872, 214, 138, 116, 16); ctx.fill();
    ctx.save(); ctx.translate(872, 214); ctx.rotate((-12 * Math.PI) / 180);
    ctx.fillStyle = "#fff"; ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    ctx.font = `700 60px ${FD}`; ctx.fillText(String(YEAR), 0, -4);
    setSpacing(ctx, 4); ctx.font = `600 34px ${FD}`; ctx.fillStyle = C.joy; ctx.fillText("WINNER", 0, 40); setSpacing(ctx, 0);
    ctx.restore();
    ctx.fillStyle = C.root; rr(ctx, 90, 905, 900, 135, 67); ctx.fill();
    ctx.textAlign = "center";
    if (d.showName) {
      ctx.fillStyle = "#fff"; fitSize(ctx, d.name, "700", FD, 54, 780); ctx.fillText(d.name, 540, 966);
      ctx.fillStyle = C.joy; ctx.font = `600 34px ${FD}`; ctx.fillText("#BlackGirlJoyChallenge Winner", 540, 1014);
    } else {
      ctx.fillStyle = C.joy; fitSize(ctx, "#BlackGirlJoyChallenge", "700", FD, 66, 780); ctx.fillText("#BlackGirlJoyChallenge", 540, 995);
    }
  },
};

export function drawFrame(ctx, img, transform, frame, opts = {}) {
  const t = { scale: 1, rotate: 0, offsetX: 0, offsetY: 0, flipH: false, ...transform };
  const S = { img, zoom: t.scale, rot: t.rotate, flip: t.flipH, offsetX: t.offsetX, offsetY: t.offsetY };
  const name = (opts.name || "").trim();
  const d = { name, showName: !!opts.showName && !!name };
  ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  (DRAW[frame.id] || DRAW.ring)(ctx, d, S);
}
